<template>
  <div
    class="smart-filters"
    @keydown.enter.capture.prevent="onFilter"
  >
    <el-row
      v-if="activeKeys.length"
      :gutter="16"
      :class="{ 'sf-mt': fixedFields.length }"
    >
      <el-col
        v-for="key in activeKeys.sort((a, b) => {
          const fieldA = getField(a);
          const fieldB = getField(b);
          return (fieldA.order || 100) - (fieldB.order || 100);
        })"
        :key="key"
        :span="getField(key).span || 5"
        class="sf-dyn-col"
      >
        <div class="sf-dyn-header">
          <span class="label">{{ getField(key).label }}</span>
          <el-button
            v-if="!fixedFields.some((f) => f.key === key)"
            plain
            type="danger"
            size="small"
            round
            :icon="IconClose"
            @click="removeFilter(key)"
          />
        </div>
        <smart-filter-control
          :field="getField(key)"
          :value="getVal(getField(key))"
          :options="optionsCache[key] || getField(key).options || []"
          :loading="!!loadingMap[key]"
          @update="updateFilter(getField(key), $event)"
          @open="triggerFetch(getField(key))"
        />
      </el-col>
    </el-row>

    <div :class="['sf-actions', { 'sf-mt': fixedFields.length || activeKeys.length }]">
      <el-dropdown
        v-if="availableFields.length"
        trigger="click"
        @command="addFilter"
      >
        <el-button
          type="info"
          size="default"
          class="sf-add-btn"
        >
          <el-icon style="margin-right: 4px"><IconPlus /></el-icon>
          Agregar filtro
          <span
            v-if="activeDynamicCount"
            class="sf-active-count"
            >({{ activeDynamicCount }})</span
          >
        </el-button>
        <template #dropdown>
          <el-dropdown-menu>
            <template v-if="hasGroups">
              <template
                v-for="(groupFields, groupName) in availableFieldsByGroup"
                :key="groupName"
              >
                <el-dropdown-item
                  v-if="groupName"
                  disabled
                  class="sf-group-label"
                >
                  {{ groupName }}
                </el-dropdown-item>
                <el-dropdown-item
                  v-for="f in groupFields"
                  :key="f.key"
                  :command="f.key"
                  :class="{ 'sf-group-item': groupName }"
                >
                  {{ f.label }}
                </el-dropdown-item>
              </template>
            </template>
            <template v-else>
              <el-dropdown-item
                v-for="f in availableFields"
                :key="f.key"
                :command="f.key"
              >
                {{ f.label }}
              </el-dropdown-item>
            </template>
          </el-dropdown-menu>
        </template>
      </el-dropdown>

      <span class="sf-spacer" />

      <slot name="filter-summary" />

      <el-popover
        placement="top"
        effect="dark"
        trigger="hover"
        content="Limpiar filtros"
        :width="130"
      >
        <template #reference>
          <el-button
            type="danger"
            size="default"
            :icon="IconDelete"
            @click="onClear"
          />
        </template>
      </el-popover>

      <el-button
        type="primary"
        size="default"
        @click="onFilter"
      >
        Filtrar
      </el-button>
    </div>
  </div>
</template>

<script setup>
import {
  ref,
  computed,
  watch,
  onBeforeMount,
} from 'vue';
import IconClose from '~icons/mdi/close';
import IconPlus from '~icons/mdi/plus';
import IconDelete from '~icons/mdi/delete';
import SmartFilterControl from './smart_filter_control.vue';

// schema type definition
// @type {Array<{
//  key: string,
//  label: string,
//  type: 'input' | 'input_number' | 'select' | 'date' | 'date_range' | 'select_input' | 'switch' | 'autocomplete',
//  placeholder: string,
//  span: number 1-24,
//  fixed: boolean,
//  group: string,
//  options: Array,
//  fetchOptions: Function,
//  fieldKey: string,
//  fieldLabel: string,
//  multiple: boolean,
//  searchFn: Function,
//  minChars: number,
//  selectWidth: number,
//  order: number,
// }>}

const props = defineProps({
  schema: {
    type: Array,
    required: true,
  },
  modelValue: {
    type: Object,
    required: true,
  },
  defaultFilters: {
    type: Object,
    required: true,
  },
  storageKey: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['update:modelValue', 'filter', 'clear']);

const activeKeys = ref([]);
const optionsCache = ref({});
const loadingMap = ref({});

const STORAGE_PREFIX = 'smart_filters_';

const getUserId = () => {
  try {
    const user = JSON.parse(localStorage.getItem('usuario'));
    return user?.Codigo ?? '';
  } catch {
    return '';
  }
};

const saveActiveKeys = () => {
  if (!props.storageKey) return;
  const userId = getUserId();
  if (!userId) return;
  try {
    localStorage.setItem(
      `${STORAGE_PREFIX}${userId}_${props.storageKey}`,
      JSON.stringify(activeKeys.value),
    );
  } catch {}
};

const loadActiveKeys = () => {
  if (!props.storageKey) return;
  const userId = getUserId();
  if (!userId) return;
  try {
    const saved = JSON.parse(
      localStorage.getItem(`${STORAGE_PREFIX}${userId}_${props.storageKey}`),
    );
    if (Array.isArray(saved)) {
      saved.forEach((k) => {
        if (props.schema.some((f) => f.key === k) && !activeKeys.value.includes(k)) {
          activeKeys.value.push(k);
        }
      });
    }
  } catch {}
};

const fixedFields = computed(() => props.schema.filter((f) => f.fixed));
const nonFixedFields = computed(() => props.schema.filter((f) => !f.fixed));

const availableFields = computed(() =>
  nonFixedFields.value
    .filter((f) => !activeKeys.value.includes(f.key))
    .sort((a, b) => a.label.localeCompare(b.label)),
);

const hasGroups = computed(() => availableFields.value.some((f) => f.group));

const availableFieldsByGroup = computed(() => {
  const groups = {};
  availableFields.value.forEach((f) => {
    const g = f.group || '';
    if (!groups[g]) groups[g] = [];
    groups[g].push(f);
  });
  return groups;
});

const activeDynamicCount = computed(
  () =>
    activeKeys.value.filter((key) => {
      const val = props.modelValue[key];
      return (
        val !== null && val !== undefined && val !== '' && !(Array.isArray(val) && val.length === 0)
      );
    }).length,
);

const getField = (key) => props.schema.find((f) => f.key === key);

const getVal = (field) => {
  if (['select_input', 'date_range'].includes(field.type)) {
    return [
      props.modelValue[field.key] ? props.modelValue[field.key].value[0] : null,
      props.modelValue[field.key] ? props.modelValue[field.key].value[1] : null,
    ];
  }
  return props.modelValue[field.key] ? props.modelValue[field.key].value : null;
};

const updateFilter = (field, value) => {
  emit('update:modelValue', {
    ...JSON.parse(JSON.stringify(props.modelValue)),
    [field.key]: {
      field,
      value: value ?? null,
    },
  });
};

const addFilter = (key) => {
  if (!activeKeys.value.includes(key)) {
    activeKeys.value.push(key);
  }
};

const removeFilter = (key) => {
  activeKeys.value = activeKeys.value.filter((k) => k !== key);
  const filters = Object.fromEntries(
    Object.entries(props.modelValue).filter(([k]) => activeKeys.value.includes(k)),
  );
  emit('update:modelValue', filters);
};

const triggerFetch = async (field) => {
  if (!field.fetchOptions) return;
  if (optionsCache.value[field.key]) return;
  loadingMap.value = { ...loadingMap.value, [field.key]: true };
  try {
    const data = await field.fetchOptions();
    optionsCache.value = { ...optionsCache.value, [field.key]: data || [] };
  } catch (e) {
    console.error(`[smart_filters] Error fetching options for "${field.key}"`, e);
    optionsCache.value = { ...optionsCache.value, [field.key]: [] };
  } finally {
    const { [field.key]: _, ...rest } = loadingMap.value;
    loadingMap.value = rest;
  }
};

const onFilter = () => {
  emit('filter');
};

const onClear = () => {
  const resetFilters = { ...props.modelValue };
  props.schema.forEach((field) => {
    if (field.type === 'date_range') {
      resetFilters[field.keyDesde] = props.defaultFilters[field.keyDesde] ?? null;
      resetFilters[field.keyHasta] = props.defaultFilters[field.keyHasta] ?? null;
    } else if (field.type === 'select_input') {
      resetFilters[field.keySelect] = props.defaultFilters[field.keySelect] ?? null;
      resetFilters[field.keyInput] = props.defaultFilters[field.keyInput] ?? null;
    } else {
      resetFilters[field.key] =
        props.defaultFilters[field.key] !== undefined ? props.defaultFilters[field.key] : null;
    }
  });
  activeKeys.value = [];
  addFixedFields();
  emit('update:modelValue', resetFilters);
  emit('clear');
};

const addFixedFields = () => {
  fixedFields.value.forEach((field) => {
    activeKeys.value.push(field.key);
  });
};

onBeforeMount(() => {
  addFixedFields();
  loadActiveKeys();
});

watch(activeKeys, saveActiveKeys, { deep: true });
</script>

<style scoped>
.smart-filters {
  background-color: white;
  border: 3px solid lightgray;
  padding: 20px;
}

.sf-label {
  font-weight: 700;
  color: #111;
  font-size: 13px;
  margin-bottom: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.sf-mt {
  margin-top: 16px;
}

.sf-dyn-col {
  margin-bottom: 4px;
}

.sf-dyn-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 4px;
  min-height: 22px;
}

.sf-dyn-header .sf-label {
  margin-bottom: 0;
}

.sf-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.sf-spacer {
  flex: 1;
}

.sf-add-btn {
  gap: 0;
}

.sf-active-count {
  margin-left: 6px;
  font-weight: 700;
  font-size: 12px;
  color: var(--el-color-primary);
}

.sf-group-label {
  font-size: 11px !important;
  font-weight: 700 !important;
  color: #999 !important;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  pointer-events: none;
  padding-left: 12px !important;
}

.sf-group-item {
  padding-left: 20px !important;
}
</style>
